
import { Component, Vue, Watch } from 'vue-property-decorator'
import { GetLeakById, ReportLeak, UpdateLeakDetection } from '@/api/leaks'
import { ILeakPoint, ILeakFile, FileType, IPipeDiameter, IPipeMaterial, IRoadSurface, LeakStatus, ILeakDetection } from '@/api/types'
import { ListPipeDiameters, ListPipeMaterials, ListRoadSurfaces } from '@/api/baseData'
import LeakStatusTag from '@/views/leaks/components/LeakStatusTag/index.vue'
import LeakMediaUpload from '@/views/leaks/components/LeakMediaUpload/index.vue'
import { IsMobile } from '@/utils/check'
import { goBack } from '@/router'
@Component({
  name: 'LeakDetectionEdit',
  components: {
    LeakStatusTag,
    LeakMediaUpload
  }
})
export default class extends Vue {
  private loading = false
  private leak: ILeakPoint | null = null
  private leakId = -1
  private isUploading = false
  private uploadProgress = 0
  private mode = 'detection'

  // 编辑表单
  private editForm = {
    detection: {
      roadInfo: '',
      size: 0,
      material: '',
      referenceAddr: '',
      detailAddr: ''
    }
  }

  // 媒体状态
  private currentMedia = {
    detectionImages: [] as string[],
    detectionVideos: [] as string[],
    referenceImages: [] as string[]
  }

  // 选项数据
  private pipeDiameterOptions: IPipeDiameter[] = []
  private pipeMaterialOptions: IPipeMaterial[] = []
  private roadSurfaceOptions: IRoadSurface[] = []

  // 验证规则
  private detectionRules = {
    roadInfo: [{ required: true, message: '请选择路面信息', trigger: 'change' }],
    size: [{ required: true, message: '请选择管径', trigger: 'change' }],
    material: [{ required: true, message: '请选择管材', trigger: 'change' }]
  }

  get isMobile() {
    return IsMobile()
  }

  private get showReportBtn(): boolean {
    return this.mode == 'report'
  }

  private get showSaveBtn(): boolean {
    return this.mode == 'detection'
  }

  get detectionImages() {
    if (!this.leak || !this.leak.detection || !this.leak.detection.files) return []
    return this.leak.detection.files
      .filter(file => file.fileType === FileType.CheckImage)
      .map(file => file.filePath)
  }

  get detectionVideos() {
    if (!this.leak || !this.leak.detection || !this.leak.detection.files) return []
    return this.leak.detection.files
      .filter(file => file.fileType === FileType.CheckVideo)
      .map(file => file.filePath)
  }

  get referenceImages() {
    if (!this.leak || !this.leak.detection || !this.leak.detection.files) return []
    return this.leak.detection.files
      .filter(file => file.fileType === FileType.ReferenceImage)
      .map(file => file.filePath)
  }

  created() {
    // 从路由参数获取漏点ID
    this.leakId = Number(this.$route.params.id)
    this.mode = this.$route.params.mode
    if (this.leakId) {

      this.fetchLeakDetail()
      this.fetchOptions()
    } else {
      this.$message.error('漏点ID不存在')
      this.handleCancel()
    }
  }

  /**
   * 获取漏点详情
   */
  private async fetchLeakDetail() {
    try {
      this.loading = true

      if (this.$route.params.leak) {
        this.leak = JSON.parse(this.$route.params.leak)
      } else {
        const response = await GetLeakById({ id: Number(this.leakId) })
        this.leak = response.data
      }

      // 初始化媒体文件列表
      this.initMediaLists()

      // 初始化编辑表单
      this.initEditForm()
    } catch (error) {
      console.error('获取漏点详情失败', error)
      this.$message.error('获取漏点详情失败')
    } finally {
      this.loading = false
    }
  }

  /**
   * 获取下拉选项数据
   */
  private async fetchOptions() {
    try {
      // 获取管径选项
      const diameterResponse = await ListPipeDiameters()
      this.pipeDiameterOptions = diameterResponse.data || []

      // 获取管材选项
      const materialResponse = await ListPipeMaterials()
      this.pipeMaterialOptions = materialResponse.data || []

      // 获取路面选项
      const roadResponse = await ListRoadSurfaces()
      this.roadSurfaceOptions = roadResponse.data || []
    } catch (error) {
      console.error('获取选项数据失败', error)
    }
  }

  /**
   * 初始化媒体文件列表
   */
  private initMediaLists() {
    if (!this.leak) return

    // 检测媒体
    this.currentMedia.detectionImages = this.detectionImages
    this.currentMedia.detectionVideos = this.detectionVideos
    this.currentMedia.referenceImages = this.referenceImages
  }

  /**
   * 初始化编辑表单
   */
  private initEditForm() {
    if (!this.leak || !this.leak.detection) return

    this.editForm.detection = {
      roadInfo: this.leak.detection.roadInfo || (this.roadSurfaceOptions.length > 0 ? this.roadSurfaceOptions[0].name : ''),
      size: this.leak.detection.size || (this.pipeDiameterOptions.length > 0 ? this.pipeDiameterOptions[0].size : 0),
      material: this.leak.detection.material || (this.pipeMaterialOptions.length > 0 ? this.pipeMaterialOptions[0].name : ''),
      referenceAddr: this.leak.detection.referenceAddr || '',
      detailAddr: this.leak.detection.detailAddr || ''
    }
  }

  /**
   * 组装保存参数
   */
  private assembleSaveParams() {
      const detectionMediaUpload = this.$refs.detectionMediaUpload as any;
      const currentMedia = detectionMediaUpload.getCurrentMedia ? detectionMediaUpload.getCurrentMedia() : {
        checkImages: this.currentMedia.detectionImages,
        checkVideos: this.currentMedia.detectionVideos,
        referenceImages: this.currentMedia.referenceImages
      };

      let params = {
        id: Number(this.leakId),
        roadInfo: this.editForm.detection.roadInfo,
        size: this.editForm.detection.size,
        material: this.editForm.detection.material,
        detailAddr: this.editForm.detection.detailAddr,
        files: [] as ILeakFile[]
      }

      // 添加文件
      if (currentMedia.checkImages.length > 0) {
        currentMedia.checkImages.forEach((image: string) => {
          params.files.push({
            filePath: image,
            fileType: FileType.CheckImage,
          })
        })
      }

      if (currentMedia.checkVideos.length > 0) {
        currentMedia.checkVideos.forEach((video: string) => {
          params.files.push({
            filePath: video,
            fileType: FileType.CheckVideo,
          })
        })
      }

      if (currentMedia.referenceImages.length > 0) {
        currentMedia.referenceImages.forEach((image: string) => {
          params.files.push({
            filePath: image,
            fileType: FileType.ReferenceImage,
          })
        })
    }
    return params
  }


  /**
   * 上报漏点
   */
  private async submitReportLeak() {
    if (this.isUploading) {
      this.$message.warning('请等待文件上传完成后再提交')
      return
    }

    // 表单验证
    try {
      await (this.$refs.detectionForm as any).validate()
    } catch (error) {
      return
    }

    if (!this.isDetectionMediaComplete()) {
      return
    }

    try {
      this.loading = true

      const params = this.assembleSaveParams()

      await ReportLeak(params)
      this.$message.success('上报成功')

      goBack()
    } catch (error) {
      console.error('上报漏点失败', error)
      this.$message.error('上报漏点失败')
    } finally {
      this.loading = false
    }
  }

  /**
   * 提交编辑
   */
  private async submitEdit() {
    if (this.isUploading) {
      this.$message.warning('请等待文件上传完成后再提交')
      return
    }

    // 表单验证
    try {
      await (this.$refs.detectionForm as any).validate()
    } catch (error) {
      return false
    }

    try {
      this.loading = true

      const params = this.assembleSaveParams()
      await UpdateLeakDetection(params)
      this.$message.success('检测信息更新成功')

      goBack()
    } catch (error) {
      console.error('提交检测信息编辑失败', error)
      this.$message.error('提交检测信息编辑失败')
    } finally {
      this.loading = false
    }
  }

  /**
   * 处理检测媒体变更
   */
  private handleDetectionMediaChange(media: { checkImages: string[], checkVideos: string[], referenceImages: string[] }) {
    if (!this.leak) return;

    console.log('检测媒体数据变更', media);
    this.currentMedia.detectionImages = media.checkImages
    this.currentMedia.detectionVideos = media.checkVideos
    this.currentMedia.referenceImages = media.referenceImages

    // 检查是否有媒体文件，更新界面提示
    // this.updateDetectionMediaTip(media);
  }

  /**
   * 检测上报媒体信息是否完整
   */
  private isDetectionMediaComplete(): boolean {
    const media = this.currentMedia
    if (media.detectionImages.length === 0) {
      this.$message.warning('请上传至少一张检测图片')
      return false
    }
    if (media.detectionVideos.length === 0) {
      this.$message.warning('请上传至少一个检测视频')
      return false
    }
    if (media.referenceImages.length === 0) {
      this.$message.warning('请上传至少一张参考图片')
      return false
    }
    return true
  }

  /**
   * 更新检测媒体提示
   */
  // private updateDetectionMediaTip(media: { checkImages: string[], checkVideos: string[], referenceImages: string[] }) {
  //   // 在这里可以根据媒体数据更新UI提示
  //   const tipEl = document.querySelector('.detection-media-tip .el-alert');
  //   if (!tipEl) return;

  //   // 检查所有媒体类型是否都有至少一项
  //   const hasCheckImages = media && media.checkImages && media.checkImages.length > 0;
  //   const hasCheckVideos = media && media.checkVideos && media.checkVideos.length > 0;
  //   const hasReferenceImages = media && media.referenceImages && media.referenceImages.length > 0;

  //   if (hasCheckImages && hasCheckVideos && hasReferenceImages) {
  //     // 所有类型都已上传
  //     tipEl.classList.remove('el-alert--warning');
  //     tipEl.classList.add('el-alert--success');
  //     (tipEl.querySelector('.el-alert__title') as HTMLElement).innerText = '检测媒体文件 (已完成) ✓';

  //     // 更新描述文本
  //     const descEl = tipEl.querySelector('.el-alert__description') as HTMLElement;
  //     if (descEl) {
  //       descEl.innerHTML = `
  //         <div>已上传全部必要媒体文件：</div>
  //         <ul style="margin: 5px 0; padding-left: 20px;">
  //           <li>检测图片：${media.checkImages.length}张 ✓</li>
  //           <li>检测视频：${media.checkVideos.length}个 ✓</li>
  //           <li>参考图片：${media.referenceImages.length}张 ✓</li>
  //         </ul>
  //       `;
  //     }
  //   } else {
  //     // 有类型未上传，保持警告状态但更新状态
  //     const descEl = tipEl.querySelector('.el-alert__description') as HTMLElement;
  //     if (descEl) {
  //       descEl.innerHTML = `
  //         <div>请上传媒体文件，以下各类型至少需要一个：</div>
  //         <ul style="margin: 5px 0; padding-left: 20px;">
  //           <li>检测图片：${hasCheckImages ? '已上传' + media.checkImages.length + '张 ✓' : '至少需要1张 ✗'}</li>
  //           <li>检测视频：${hasCheckVideos ? '已上传' + media.checkVideos.length + '个 ✓' : '至少需要1个 ✗'}</li>
  //           <li>参考图片：${hasReferenceImages ? '已上传' + media.referenceImages.length + '张 ✓' : '至少需要1张 ✗'}</li>
  //         </ul>
  //       `;
  //     }
  //   }
  // }

  /**
   * 处理上传状态变更
   */
  private handleUploadStatus(status: { uploading: boolean, progress?: number }) {
    this.isUploading = status.uploading
    if (status.progress !== undefined) {
      this.uploadProgress = status.progress
    }
  }

  /**
   * 取消编辑，返回上一页
   */
  private handleCancel() {
    goBack()
  }
}
